import React, { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import { Save, Upload, Trash2, Download, FileText, FileCheck, GraduationCap, ImageIcon, FilePlus, User as UserIcon, Eye, EyeOff, Landmark } from "lucide-react";

const SLOT_CONFIG = [
  { id: "profile_picture", label: "Profile Picture", icon: ImageIcon, accept: "image/*", hint: "JPG or PNG, up to 5 MB" },
  { id: "cv_file", label: "CV / Resume", icon: FileCheck, accept: ".pdf,.doc,.docx,.txt,.md,application/pdf", hint: "PDF, DOCX, or TXT — text auto-extracted" },
  { id: "motivation_letter", label: "Motivation Letter", icon: FileText, accept: ".pdf,.doc,.docx,.txt,.md,application/pdf", hint: "PDF, DOCX, or TXT" },
  { id: "diploma", label: "Diploma", icon: GraduationCap, accept: "image/*,.pdf,application/pdf", hint: "PDF or image" },
];

function humanSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 102.4) / 10} KB`;
  return `${Math.round(bytes / (1024 * 102.4)) / 10} MB`;
}

function useAuthedBlobUrl(docId) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let canceled = false;
    let objUrl = null;
    if (!docId) return;
    api
      .get(`/profile/documents/${docId}/download`, { responseType: "blob" })
      .then((r) => {
        if (canceled) return;
        objUrl = URL.createObjectURL(r.data);
        setUrl(objUrl);
      })
      .catch(() => {});
    return () => {
      canceled = true;
      if (objUrl) URL.revokeObjectURL(objUrl);
    };
  }, [docId]);
  return url;
}

function DocumentSlot({ slot, doc, onUpload, onDelete, onDownload }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const openPicker = () => inputRef.current?.click();

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Max 5 MB");
      return;
    }
    setBusy(true);
    try {
      await onUpload(slot.id, f);
      toast.success(`${slot.label} uploaded`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const Icon = slot.icon;
  const imageUrl = useAuthedBlobUrl(doc && doc.content_type?.startsWith("image/") ? doc.id : null);

  return (
    <div className="border border-black/10 bg-white p-5 flex flex-col gap-4" data-testid={`doc-slot-${slot.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          {slot.id === "profile_picture" && imageUrl ? (
            <img
              src={imageUrl}
              alt="avatar"
              className="w-14 h-14 object-cover border border-black/10"
              data-testid="profile-picture-preview"
            />
          ) : (
            <div className="w-14 h-14 bg-[#F8F9FA] border border-black/10 flex items-center justify-center">
              <Icon className="w-6 h-6 stroke-[1.5]" />
            </div>
          )}
          <div>
            <div className="font-display font-bold">{slot.label}</div>
            <div className="text-xs text-gray-500">{slot.hint}</div>
          </div>
        </div>
      </div>

      {doc ? (
        <div className="bg-[#F8F9FA] border border-black/10 p-3 flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium truncate">{doc.filename}</div>
            <div className="font-mono text-[10px] text-gray-500 uppercase tracking-widest">
              {humanSize(doc.size)} · {new Date(doc.uploaded_at).toLocaleDateString()}
            </div>
          </div>
          <button
            onClick={() => onDownload(doc)}
            className="p-2 border border-black/10 hover:bg-black hover:text-white"
            title="Download"
            data-testid={`download-${slot.id}`}
          >
            <Download className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={openPicker}
            className="p-2 border border-black/10 hover:bg-black hover:text-white"
            title="Replace"
            data-testid={`replace-${slot.id}`}
          >
            <Upload className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => onDelete(doc.id)}
            className="p-2 border border-black/10 hover:bg-[#FF3B30] hover:border-[#FF3B30] hover:text-white"
            title="Delete"
            data-testid={`delete-${slot.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <button
          onClick={openPicker}
          disabled={busy}
          className="border-2 border-dashed border-black/20 hover:border-black hover:bg-[#F8F9FA] py-5 flex flex-col items-center gap-1 transition-colors disabled:opacity-60"
          data-testid={`upload-${slot.id}`}
        >
          <Upload className="w-5 h-5" />
          <span className="text-sm font-bold">{busy ? "Uploading…" : "Upload"}</span>
        </button>
      )}

      <input ref={inputRef} type="file" accept={slot.accept} className="hidden" onChange={handleFile} />
    </div>
  );
}

export default function Profile() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [form, setForm] = useState({
    headline: "",
    skills: "",
    job_titles: "",
    preferred_cities: "",
    languages: "",
    experience_years: 0,
    cv_text: "",
    cv_filename: "",
    phone: "",
    location: "",
    nationality: "Saudi",
    is_saudi: true,
    gender: "",
    age: 0,
    has_disability: false,
    discoverable: false,
    looking_for_work: false,
    hrdf_opt_in: false,
    target_salary_sar: 0,
  });
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const otherInputRef = useRef(null);
  const [otherBusy, setOtherBusy] = useState(false);
  const [gmail, setGmail] = useState({ connected: false, email: null, configured: false });
  const [gmailBusy, setGmailBusy] = useState(false);

  // Handle OAuth redirect flash (?gmail=connected|error)
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const status = p.get("gmail");
    if (status === "connected") toast.success("Gmail connected! Maqam can now send from your email.");
    else if (status === "error") toast.error(`Gmail connection failed: ${p.get("reason") || "unknown"}`);
    if (status) {
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  // Load Gmail status
  const refreshGmail = () => api.get("/oauth/gmail/status").then((r) => setGmail(r.data)).catch(() => {});
  useEffect(() => { refreshGmail(); }, []);

  const connectGmail = async () => {
    setGmailBusy(true);
    try {
      const { data } = await api.get("/oauth/gmail/login");
      window.location.href = data.auth_url;
    } catch (e) {
      toast.error("Could not start Gmail connection.");
      setGmailBusy(false);
    }
  };
  const disconnectGmail = async () => {
    await api.post("/oauth/gmail/disconnect");
    toast.success("Gmail disconnected.");
    refreshGmail();
  };
  const testGmail = async () => {
    setGmailBusy(true);
    try {
      await api.post("/oauth/gmail/test");
      toast.success(`Test email sent to ${gmail.email}. Check your inbox!`);
    } catch (e) {
      toast.error("Test send failed. Try reconnecting Gmail.");
    } finally {
      setGmailBusy(false);
    }
  };

  useEffect(() => {
    api.get("/profile").then((r) => {
      const p = r.data;
      setForm({
        headline: p.headline || "",
        skills: (p.skills || []).join(", "),
        job_titles: (p.job_titles || []).join(", "),
        preferred_cities: (p.preferred_cities || []).join(", "),
        languages: (p.languages || []).join(", "),
        experience_years: p.experience_years || 0,
        cv_text: p.cv_text || "",
        cv_filename: p.cv_filename || "",
        phone: p.phone || "",
        location: p.location || "",
        nationality: p.nationality || "Saudi",
        is_saudi: p.is_saudi !== false,
        gender: p.gender || "",
        age: p.age || 0,
        has_disability: !!p.has_disability,
        discoverable: !!p.discoverable,
        looking_for_work: !!p.looking_for_work,
        hrdf_opt_in: !!p.hrdf_opt_in,
        target_salary_sar: p.target_salary_sar || 0,
      });
    }).catch(() => {});
    loadDocs();
  }, []);

  const loadDocs = () => api.get("/profile/documents").then((r) => setDocuments(r.data)).catch(() => {});

  const refreshProfile = () =>
    api.get("/profile").then((r) => {
      const p = r.data;
      setForm((s) => ({
        ...s,
        cv_text: p.cv_text || s.cv_text,
        cv_filename: p.cv_filename || s.cv_filename,
      }));
    }).catch(() => {});

  const onPasteCv = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const text = await f.text();
    setForm((s) => ({ ...s, cv_text: text, cv_filename: f.name }));
    toast.success(`CV text loaded: ${f.name}`);
  };

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    const payload = {
      ...form,
      skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
      job_titles: form.job_titles.split(",").map((s) => s.trim()).filter(Boolean),
      preferred_cities: form.preferred_cities.split(",").map((s) => s.trim()).filter(Boolean),
      languages: form.languages.split(",").map((s) => s.trim()).filter(Boolean),
      experience_years: parseInt(form.experience_years) || 0,
      age: parseInt(form.age) || 0,
      target_salary_sar: parseInt(form.target_salary_sar) || 0,
    };
    try {
      await api.put("/profile", payload);
      toast.success(t("saved"));
    } catch {
      toast.error("Save failed");
    } finally {
      setLoading(false);
    }
  };

  const uploadDoc = async (category, file, label = "") => {
    const fd = new FormData();
    fd.append("category", category);
    fd.append("file", file);
    if (label) fd.append("label", label);
    const r = await api.post("/profile/documents", fd);
    await loadDocs();
    // If this is a CV file, the backend extracted the text server-side; refresh
    // the form so the user can see their cv_text populated.
    if (category === "cv_file") {
      await refreshProfile();
      const chars = r.data?.extracted_chars || 0;
      if (chars > 0) toast.success(`CV parsed · ${chars.toLocaleString()} characters extracted`);
    }
  };

  const deleteDoc = async (id) => {
    await api.delete(`/profile/documents/${id}`);
    toast.success("Deleted");
    loadDocs();
  };

  const downloadDoc = async (doc) => {
    try {
      const r = await api.get(`/profile/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const handleOtherFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setOtherBusy(true);
    try {
      await uploadDoc("other", f);
      toast.success("Document uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setOtherBusy(false);
      e.target.value = "";
    }
  };

  const docByCategory = (cat) => documents.find((d) => d.category === cat);
  const otherDocs = documents.filter((d) => d.category === "other" || d.category === "certificate");
  const input = "w-full bg-transparent border-b-2 border-gray-200 focus:border-black outline-none py-3";

  return (
    <form onSubmit={save} className="space-y-10 max-w-4xl" data-testid="profile-form">
      <div>
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-gray-500 mb-3">/ {t("profile")}</div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">Your profile</h1>
      </div>

      <div className="border border-black/10 bg-[#F8F9FA] p-6">
        <div className="font-mono text-xs uppercase tracking-widest text-gray-500 mb-2">{t("your_platform_email")}</div>
        <div className="font-display font-black text-2xl text-[#0033FF] break-all">{user?.platform_email}</div>
        <p className="text-sm text-gray-600 mt-2">All employer replies arrive in your Inbox.</p>
      </div>

      {/* Gmail OAuth — send job apps from the user's own email */}
      {process.env.REACT_APP_GOOGLE_AUTH_ENABLED === "true" ? (
        <section className="border border-black/10 bg-white p-6" data-testid="gmail-connect-card">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#0033FF] mb-2">Connected Accounts</div>
            <h2 className="font-display font-bold text-2xl">Gmail</h2>
            <p className="text-sm text-gray-600 mt-2 max-w-xl leading-relaxed">
              Connect your Gmail and Maqam will send your bulk job applications from <b>your real email</b>.
              Inbox delivery jumps from ~40% to 99%. Maqam can only send — it cannot read, delete, or modify your emails.
            </p>
            {gmail.connected && (
              <div className="mt-4 inline-flex items-center gap-3 bg-[#F8F9FA] border border-black/10 px-4 py-2 rounded-full text-sm">
                <span className="w-2 h-2 rounded-full bg-green-500" />
                <span>Connected as <b>{gmail.email}</b></span>
              </div>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
            {!gmail.configured ? (
              <span className="text-xs text-gray-500 self-center">Gmail OAuth unavailable.</span>
            ) : gmail.connected ? (
              <>
                <button
                  type="button"
                  onClick={testGmail}
                  disabled={gmailBusy}
                  className="px-5 py-2.5 border border-black/15 text-sm font-medium hover:bg-black/5 transition disabled:opacity-50"
                  data-testid="gmail-test-btn"
                >
                  Send test email
                </button>
                <button
                  type="button"
                  onClick={disconnectGmail}
                  disabled={gmailBusy}
                  className="px-5 py-2.5 border border-white/20 text-white/70 text-sm font-medium hover:bg-white/5 transition disabled:opacity-50"
                  data-testid="gmail-disconnect-btn"
                >
                  Disconnect
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={connectGmail}
                disabled={gmailBusy}
                className="px-6 py-3 bg-black text-white text-sm font-semibold hover:bg-[#C9A961] hover:text-black transition disabled:opacity-50 flex items-center gap-2 rounded-full"
                data-testid="gmail-connect-btn"
              >
                {gmailBusy ? "Starting…" : "Connect Gmail"}
              </button>
            )}
          </div>
        </div>
      </section>
      ) : (
        <section className="border border-white/10 bg-white/[0.03] p-6 rounded-xl" data-testid="gmail-coming-soon">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-white/[0.06] border border-white/10 rounded flex items-center justify-center text-lg shrink-0">✉</div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2">Connected Accounts</div>
              <h2 className="font-display font-bold text-xl">Gmail integration — coming soon</h2>
              <p className="text-sm text-white/60 mt-2 max-w-xl leading-relaxed">
                We're in Google's verification queue. Once approved, you'll be able to connect your personal Gmail
                so applications go from <b>your real email</b> with ~99% inbox rate.
                <br/>
                Meanwhile, your applications are sent through Maqam's outbound infrastructure with replies forwarded
                to your platform inbox <b>{user?.platform_email}</b>.
              </p>
            </div>
          </div>
        </section>
      )}
      <section className="space-y-5">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#0033FF] mb-1">Document vault</div>
            <h2 className="font-display font-bold text-2xl">Your documents</h2>
            <p className="text-sm text-gray-600 mt-1">
              Store your profile picture, CV, diplomas, certificates — attached to your applications when relevant.
            </p>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {SLOT_CONFIG.map((slot) => (
            <DocumentSlot
              key={slot.id}
              slot={slot}
              doc={docByCategory(slot.id)}
              onUpload={uploadDoc}
              onDelete={deleteDoc}
              onDownload={downloadDoc}
            />
          ))}
        </div>

        {/* Other documents (multiple) */}
        <div className="border border-black/10 bg-white p-5 space-y-4" data-testid="other-docs-card">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#F8F9FA] border border-black/10 flex items-center justify-center">
                <FilePlus className="w-5 h-5 stroke-[1.5]" />
              </div>
              <div>
                <div className="font-display font-bold">Important documents</div>
                <div className="text-xs text-gray-500">Certificates, licenses, references — upload as many as you need (max 5 MB each)</div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => otherInputRef.current?.click()}
              disabled={otherBusy}
              className="bg-black text-white hover:bg-[#0033FF] font-bold px-4 py-2 text-sm flex items-center gap-2 transition-colors disabled:opacity-60"
              data-testid="upload-other"
            >
              <Upload className="w-3.5 h-3.5" />
              {otherBusy ? "Uploading…" : "Add document"}
            </button>
            <input ref={otherInputRef} type="file" className="hidden" onChange={handleOtherFile} />
          </div>
          {otherDocs.length === 0 ? (
            <div className="border border-dashed border-black/15 p-6 text-center text-sm text-gray-500">
              No extra documents yet.
            </div>
          ) : (
            <div className="divide-y divide-black/10 border border-black/10">
              {otherDocs.map((d) => (
                <div key={d.id} className="flex items-center justify-between p-3 gap-2">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <FileText className="w-4 h-4 text-gray-500" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{d.filename}</div>
                      <div className="font-mono text-[10px] text-gray-500 uppercase tracking-widest">
                        {humanSize(d.size)} · {new Date(d.uploaded_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => downloadDoc(d)}
                    className="p-2 border border-black/10 hover:bg-black hover:text-white"
                    data-testid={`download-other-${d.id}`}
                  >
                    <Download className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteDoc(d.id)}
                    className="p-2 border border-black/10 hover:bg-[#FF3B30] hover:border-[#FF3B30] hover:text-white"
                    data-testid={`delete-other-${d.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Basic fields */}
      <div className="grid md:grid-cols-2 gap-6">
        <label>
          <span className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">Headline</span>
          <input
            className={input}
            placeholder="Senior Backend Engineer"
            value={form.headline}
            onChange={(e) => setForm({ ...form, headline: e.target.value })}
            data-testid="profile-headline"
          />
        </label>
        <label>
          <span className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">Phone</span>
          <input className={input} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </label>
        <label>
          <span className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">{t("experience")}</span>
          <input
            type="number"
            min={0}
            className={input}
            value={form.experience_years}
            onChange={(e) => setForm({ ...form, experience_years: e.target.value })}
            data-testid="profile-experience"
          />
        </label>
        <label>
          <span className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">Current location</span>
          <input
            className={input}
            placeholder="Riyadh, KSA"
            value={form.location}
            onChange={(e) => setForm({ ...form, location: e.target.value })}
          />
        </label>
        <label className="md:col-span-2">
          <span className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">{t("skills")}</span>
          <input
            className={input}
            placeholder="Python, React, PostgreSQL, AWS"
            value={form.skills}
            onChange={(e) => setForm({ ...form, skills: e.target.value })}
            data-testid="profile-skills"
          />
        </label>
        <label className="md:col-span-2">
          <span className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">{t("job_titles")}</span>
          <input
            className={input}
            placeholder="Backend Engineer, Software Architect"
            value={form.job_titles}
            onChange={(e) => setForm({ ...form, job_titles: e.target.value })}
            data-testid="profile-job-titles"
          />
        </label>
        <label className="md:col-span-2">
          <span className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">{t("preferred_cities")}</span>
          <input
            className={input}
            placeholder="Riyadh, Jeddah, Dammam"
            value={form.preferred_cities}
            onChange={(e) => setForm({ ...form, preferred_cities: e.target.value })}
          />
        </label>
        <label className="md:col-span-2">
          <span className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">Languages</span>
          <input
            className={input}
            placeholder="Arabic, English"
            value={form.languages}
            onChange={(e) => setForm({ ...form, languages: e.target.value })}
          />
        </label>
      </div>

      {/* Nitaqat section */}
      <div className="border border-black/10 bg-[#F8F9FA] p-6 space-y-5">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF3B30] mb-1">Nitaqat data</div>
          <h3 className="font-display font-bold text-xl">Saudization match (optional)</h3>
          <p className="text-xs text-gray-600 mt-1 max-w-xl">
            Helps employers see how you contribute to their Nitaqat quota. Only stored on Maqam — never sent with applications.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <label>
            <span className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">Nationality</span>
            <input
              className={input}
              value={form.nationality}
              onChange={(e) => setForm({ ...form, nationality: e.target.value, is_saudi: e.target.value.toLowerCase().includes("saudi") })}
              data-testid="profile-nationality"
            />
          </label>
          <label>
            <span className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">Gender</span>
            <select
              value={form.gender}
              onChange={(e) => setForm({ ...form, gender: e.target.value })}
              className={input}
              data-testid="profile-gender"
            >
              <option value="">Prefer not to say</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </label>
          <label>
            <span className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">Age</span>
            <input
              type="number"
              min={0}
              className={input}
              value={form.age}
              onChange={(e) => setForm({ ...form, age: e.target.value })}
              data-testid="profile-age"
            />
          </label>
          <label className="flex items-center gap-3 mt-7 cursor-pointer">
            <input
              type="checkbox"
              checked={form.has_disability}
              onChange={(e) => setForm({ ...form, has_disability: e.target.checked })}
              className="w-5 h-5 accent-[#0033FF]"
              data-testid="profile-disability"
            />
            <span className="text-sm">Person with disability (Nitaqat multiplier applies)</span>
          </label>
        </div>
      </div>

      {/* CV Text (for engine to read) */}
      <div>
        <label className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">
          CV text — used by the search engine to tailor applications
        </label>
        <div className="flex items-center gap-3 mb-3">
          <label className="inline-flex items-center gap-2 px-4 py-2 border border-black text-black hover:bg-black hover:text-white cursor-pointer text-sm font-bold transition-colors">
            Paste from .txt
            <input type="file" accept=".txt,text/plain" className="hidden" onChange={onPasteCv} data-testid="cv-upload" />
          </label>
          {form.cv_filename && <span className="font-mono text-xs text-gray-500">{form.cv_filename}</span>}
        </div>
        <textarea
          rows={10}
          className="w-full border border-black/10 p-4 focus:border-black outline-none bg-white font-mono text-sm"
          placeholder="Paste your full CV text here — the engine uses this to write tailored cover letters…"
          value={form.cv_text}
          onChange={(e) => setForm({ ...form, cv_text: e.target.value })}
          data-testid="profile-cv-text"
        />
      </div>

      {/* Shadow Candidate — recruiters find YOU */}
      <div className="border-2 border-black bg-black text-white p-6 space-y-5" data-testid="shadow-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#34C759] mb-1">
              Shadow candidate · reverse apply
            </div>
            <h3 className="font-display font-bold text-2xl">Let recruiters find you.</h3>
            <p className="text-sm text-white/70 mt-2 max-w-xl">
              Opt in and your anonymized profile (verified badges, Nitaqat score, skills, experience) shows
              up in the Recruiter Portal. You stay hidden — only your first name + last initial are visible
              until a recruiter sends you an intro in your inbox.
            </p>
          </div>
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={form.discoverable}
              onChange={(e) => setForm({ ...form, discoverable: e.target.checked })}
              className="sr-only peer"
              data-testid="profile-discoverable"
            />
            <span className="w-12 h-7 bg-white/20 peer-checked:bg-[#34C759] relative transition-colors">
              <span
                className={`absolute top-1 w-5 h-5 bg-white transition-all ${
                  form.discoverable ? "left-6" : "left-1"
                }`}
              />
            </span>
            <span className="font-bold text-sm flex items-center gap-2">
              {form.discoverable ? (
                <>
                  <Eye className="w-4 h-4 text-[#34C759]" /> Visible
                </>
              ) : (
                <>
                  <EyeOff className="w-4 h-4 text-white/60" /> Hidden
                </>
              )}
            </span>
          </label>
        </div>
        {/* Looking-for-work sub-toggle — pushes candidate to the top of
            employer search results on Maqam Hire ($700/mo employer tier). */}
        {form.discoverable && (
          <label
            className="flex items-start gap-3 cursor-pointer select-none border-t border-white/10 pt-4 mt-2"
            data-testid="looking-for-work-card"
          >
            <input
              type="checkbox"
              checked={form.looking_for_work}
              onChange={(e) => setForm({ ...form, looking_for_work: e.target.checked })}
              className="sr-only peer"
              data-testid="profile-looking-for-work"
            />
            <span className="w-11 h-6 bg-white/20 peer-checked:bg-[#FF9500] relative transition-colors shrink-0 mt-0.5">
              <span
                className={`absolute top-1 w-4 h-4 bg-white transition-all ${
                  form.looking_for_work ? "left-6" : "left-1"
                }`}
              />
            </span>
            <span>
              <span className="font-bold text-sm flex items-center gap-2">
                🔥 Actively looking right now
              </span>
              <span className="text-xs text-white/60 block mt-1 max-w-md">
                Bumps you to the top of employer searches on Maqam Hire. Recommended if you can start a new role within 60 days.
              </span>
            </span>
          </label>
        )}
        <label className="block max-w-xs">
          <span className="block font-mono text-[10px] uppercase tracking-widest text-white/60 mb-2">
            Target monthly salary (SAR)
          </span>
          <input
            type="number"
            min={0}
            className="w-full bg-transparent border-b-2 border-white/30 focus:border-[#34C759] outline-none py-3 text-white placeholder-white/40"
            placeholder="e.g. 22000"
            value={form.target_salary_sar}
            onChange={(e) => setForm({ ...form, target_salary_sar: e.target.value })}
            data-testid="profile-target-salary"
          />
        </label>
      </div>

      {/* HRDF opt-in */}
      <div className="border border-black/10 bg-[#F8F9FA] p-6 flex items-start gap-4" data-testid="hrdf-opt-card">
        <Landmark className="w-6 h-6 text-[#0033FF] shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#0033FF] mb-1">
            HRDF eligibility
          </div>
          <div className="font-display font-bold text-lg">Unlock government subsidies</div>
          <p className="text-xs text-gray-600 mt-1 max-w-xl">
            Share your eligibility with HRDF so subsidy claims (Saudization support, Wusool, Tawafuq…)
            are pre-populated when you apply. You can revoke anytime.
          </p>
        </div>
        <label className="flex items-center gap-3 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={form.hrdf_opt_in}
            onChange={(e) => setForm({ ...form, hrdf_opt_in: e.target.checked })}
            className="sr-only peer"
            data-testid="profile-hrdf-optin"
          />
          <span className="w-12 h-7 bg-black/10 peer-checked:bg-[#0033FF] relative transition-colors">
            <span
              className={`absolute top-1 w-5 h-5 bg-white transition-all ${
                form.hrdf_opt_in ? "left-6" : "left-1"
              }`}
            />
          </span>
        </label>
      </div>

      <button
        disabled={loading}
        className="bg-black text-white hover:bg-[#0033FF] font-bold px-8 py-4 flex items-center gap-3 transition-colors disabled:opacity-60"
        data-testid="profile-save"
      >
        <Save className="w-4 h-4" />
        {loading ? "…" : t("save")}
      </button>
    </form>
  );
}
